/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import cx from 'classnames';
import type { CalendarDate, DateValue } from '@internationalized/date';

import { DatePicker, localToday, useBreakpoint } from 'sarsaparilla';

import { SingleDateCalendar } from './calendars/SingleDateCalendar';
import { useScrollTopOnClear } from './calendars/date-picker-utils';
import { TypeInDatesAccordion } from './TypeInDatesAccordion';
import type { DatePickerVariant } from './DatePicker.types';
import { CALENDAR_MONTHS_COUNT, maxSelectableDate } from './DatePicker.utils';

import styles from './DatesPanel.module.scss';

// Module-level so the memoized SingleDateCalendar gets the same function every render.
function isOutsideSelectableRange(date: DateValue) {
    return date.compare(localToday) < 0 || date.compare(maxSelectableDate) > 0;
}

interface SingleDatePanelProps {
    date: CalendarDate | null;
    setDate: React.Dispatch<React.SetStateAction<CalendarDate | null>>;
    variant: DatePickerVariant;
}

/**
 * Single-date body: a collapsible typed-date field above the calendar.
 */
export function SingleDatePanel({ date, setDate, variant }: SingleDatePanelProps) {
    // Always a date: the calendar treats a null focusedValue as uncontrolled, so
    // the first month navigation would flip it to controlled.
    const [focusedValue, setFocusedValue] = React.useState<CalendarDate>(
        () => date ?? localToday
    );
    const { isSmAndUp, isSmAndDown } = useBreakpoint();
    const scrollAreaRef = React.useRef<HTMLDivElement>(null);

    // Reset returns the stacked calendar to the top. Focus moves back to today
    // too, or tabbing into the calendar would scroll to the old date again.
    useScrollTopOnClear(scrollAreaRef, date, () => {
        setFocusedValue(localToday);
    });

    return (
        <div
            className={cx(styles.DatesPanel, {
                [styles.isDrawer]: variant === 'drawer',
            })}
            tabIndex={-1}
        >
            <div ref={scrollAreaRef} className={styles.ScrollArea}>
                <TypeInDatesAccordion title="Type in date">
                    <TypeInDate
                        date={date}
                        setDate={setDate}
                        setFocusedValue={setFocusedValue}
                    />
                </TypeInDatesAccordion>

                <div className={styles.RangeCalendarContainer}>
                    <SingleDateCalendar
                        numVisibleMonths={isSmAndUp ? 2 : CALENDAR_MONTHS_COUNT}
                        layout={isSmAndUp ? 'paged' : 'stacked'}
                        initialScrollDate={date}
                        size={isSmAndDown ? 'fluid' : 'default'}
                        value={date}
                        onChange={setDate}
                        isDateUnavailable={isOutsideSelectableRange}
                        focusedValue={focusedValue}
                        onFocusChange={setFocusedValue}
                        maxValue={maxSelectableDate}
                        minValue={localToday}
                    />
                </div>
            </div>
        </div>
    );
}

interface TypeInDateProps {
    date: CalendarDate | null;
    setDate: React.Dispatch<React.SetStateAction<CalendarDate | null>>;
    setFocusedValue: React.Dispatch<React.SetStateAction<CalendarDate>>;
}

/**
 * Keyboard entry for the date. Only a complete, valid date reaches the draft,
 * so a half-typed year doesn't jump the calendar around.
 */
function TypeInDate({ date, setDate, setFocusedValue }: TypeInDateProps) {
    const [typedDate, setTypedDate] = React.useState<CalendarDate | null>(date);

    React.useEffect(() => {
        setTypedDate(date);
    }, [date]);

    return (
        <DatePicker
            label="Date"
            isLabelVisible={false}
            hasCalendar={false}
            maxValue={maxSelectableDate}
            minValue={localToday}
            hasFloatingError={true}
            value={typedDate}
            onChange={(value, validation) => {
                setTypedDate(value);

                const hasCompleteYear = value ? value.year >= 2000 : false;

                if (value && !validation?.isInvalid && hasCompleteYear) {
                    setDate(value);
                    setFocusedValue(value);
                }

                if (!value) {
                    setDate(null);
                }
            }}
            style={{
                position: 'relative',
                top: '0.1rem',
            }}
        />
    );
}
